/* Cart delete -> "Save to wishlist" or "Remove from cart".

   Covers BOTH carts. The drawer and the cart page render different
   inner controls (a <button> and an <a href=url_to_remove>), but both
   wrap it in <cart-remove-button>, and cart.js binds its click handler
   to that element in the constructor. So the element is the one thing
   worth hooking, and hooking it once covers both.

   How the interception works: cart.js listens on <cart-remove-button>
   itself, which is the event's target-phase. A listener on `document`
   in the CAPTURE phase runs before the event ever reaches the target,
   so stopPropagation() there keeps Dawn's handler from firing at all.

   To go ahead with a removal we re-dispatch the click on the same
   element behind a one-shot flag, rather than calling
   cartItems.updateQuantity() directly. That reuses Dawn's own path --
   including its loading state, section re-render and focus handling --
   instead of re-implementing it against a signature
   (line, quantity, event, name, variantId) that reads event
   .currentTarget back out to decide how to report the change. */
(function () {
  var ALLOW_FLAG = '__uicRemoveAllowed';
  var sheet = null;
  var pending = null;
  var lastFocused = null;

  function cartItemsFor(el) {
    return el.closest('cart-items') || el.closest('cart-drawer-items');
  }

  /* ---- Background scroll lock ----

     On the cart PAGE the sheet covers the viewport but the page went
     on scrolling behind it -- open the sheet, swipe, and the cart
     slides away underneath. An earlier version added a
     `uic-cart-choice-open` class for this and never wrote any CSS to
     go with it, so nothing was locked at all.

     `body { overflow: hidden }` does NOT work in this theme: <html>
     carries a height and <body> has overflow-y: auto, so body's
     overflow is not propagated to the viewport and the page keeps
     scrolling. Pinning the body with position: fixed is the only
     approach that holds, and the offset MUST be restored on close or
     the page silently jumps to the top.

     Skipped in the drawer, where the drawer already owns the lock --
     a second one here would fight it and double-restore. */
  var lockedAt = null;

  function lockScroll(shouldLock) {
    if (!shouldLock || lockedAt !== null) return;

    lockedAt = window.scrollY || window.pageYOffset || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = -lockedAt + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  function unlockScroll() {
    if (lockedAt === null) return;

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';

    window.scrollTo(0, lockedAt);
    lockedAt = null;
  }

  /* Let the real removal happen -- by whichever mechanism this page
     actually has.

     The two carts are not wired the same way. cart.js, which defines
     <cart-remove-button> and does the AJAX removal, is loaded by
     snippets/cart-drawer.liquid, but sections/main-cart-items.liquid
     wraps its own copy in `unless settings.cart_type == 'drawer'`.
     This store IS set to drawer, so on the CART PAGE cart.js never
     loads, the element is never upgraded, and removal happens by
     plain navigation to the <a href="...&quantity=0">. In the DRAWER
     the same element is upgraded and a <button> is removed over AJAX.

     So: if the custom element is defined, re-dispatch the click and
     let Dawn's own handler run -- that keeps its loading state,
     section re-render and focus handling. If it is not, follow the
     link, which is exactly what the browser would have done had we
     not intercepted it. */
  function doRemove(btn) {
    if (!btn || !btn.isConnected) return;

    if (customElements.get('cart-remove-button')) {
      // The flag is read and cleared by the capture listener below,
      // so exactly one click passes through to cart.js.
      btn[ALLOW_FLAG] = true;
      btn.click();
      return;
    }

    var link = btn.querySelector('a[href]');
    if (link) window.location.href = link.href;
  }

  /* A bottom sheet, anchored to the bottom edge and full width.

     Not a centred dialog: that version floated in the middle of the
     screen detached from everything and read as a generic alert. A
     sheet that rises from the bottom edge is the established pattern
     for "decide about this item" on a phone and keeps the cart
     visible behind it.

     Appended to <body> rather than inserted into the cart table. An
     inline row was tried and does keep the choice physically inside
     the cart, but it cannot be a bottom sheet -- a <tr> cannot be
     position: fixed without breaking table layout -- and on a long
     cart the row can open off-screen with no indication anything
     happened. */
  function buildSheet() {
    var el = document.createElement('div');
    el.className = 'uic-cart-choice';
    el.hidden = true;
    el.innerHTML =
      '<div class="uic-cart-choice__backdrop" data-uic-choice-cancel></div>' +
      '<div class="uic-cart-choice__sheet" role="dialog" aria-modal="true" aria-labelledby="UicCartChoiceTitle">' +
      '<button type="button" class="uic-cart-choice__close" data-uic-choice-cancel aria-label="Close">' +
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"/></svg>' +
      '</button>' +
      '<div class="uic-cart-choice__top">' +
      '<img class="uic-cart-choice__img" alt="" width="90" height="90" hidden>' +
      '<div class="uic-cart-choice__copy">' +
      '<p class="uic-cart-choice__title" id="UicCartChoiceTitle">Remove from cart?</p>' +
      '<p class="uic-cart-choice__sub">You can save products to your wishlist and buy them later.</p>' +
      '</div></div>' +
      '<div class="uic-cart-choice__actions">' +
      '<button type="button" class="uic-cart-choice__btn uic-cart-choice__btn--remove" data-uic-choice="remove">Remove</button>' +
      '<button type="button" class="uic-cart-choice__btn uic-cart-choice__btn--save" data-uic-choice="wishlist">Move to wishlist</button>' +
      '</div></div>';
    document.body.appendChild(el);
    return el;
  }

  function open(btn) {
    sheet = sheet || buildSheet();
    pending = btn;
    lastFocused = document.activeElement;

    var image = btn.getAttribute('data-uic-product-image') || '';
    var img = sheet.querySelector('.uic-cart-choice__img');
    img.hidden = !image;
    if (image) img.src = image;

    /* A product with no id cannot go on the wishlist -- the list is
       keyed by id -- so hide that button rather than offer one that
       would silently do nothing. */
    var canWishlist = !!btn.getAttribute('data-uic-product-id') && !!window.UICWishlist;
    sheet.querySelector('[data-uic-choice="wishlist"]').hidden = !canWishlist;

    /* Confine the sheet to the quick cart when that is where the
       delete was pressed, by moving it INSIDE the drawer panel.

       <cart-drawer> itself spans the whole viewport -- it is the
       dimming layer -- so there is nothing to inherit a width from;
       the real panel is .drawer__inner, 576px on desktop and nearly
       full width on a phone.

       An earlier version measured that panel and pinned the sheet's
       left/width to it in inline px. That is fragile: measured while
       the drawer is still sliding in, the panel reads as off-screen
       (1440-2016 on a 1440 viewport) and the sheet gets pinned out
       of sight. Re-parenting needs no measurement at all -- the
       sheet inherits the panel's position and width, tracks it
       through the open animation, and survives a window resize.
       .drawer__inner also has overflow: hidden, so the sheet is
       clipped to the cart rather than spilling over the page. */
    var drawer = btn.closest('cart-drawer');
    var panel = drawer && drawer.querySelector('.drawer__inner');

    if (panel) {
      sheet.classList.add('uic-cart-choice--drawer');
      if (sheet.parentNode !== panel) panel.appendChild(sheet);
    } else {
      // The cart PAGE has no panel to sit in, so cover the viewport.
      sheet.classList.remove('uic-cart-choice--drawer');
      if (sheet.parentNode !== document.body) document.body.appendChild(sheet);
    }

    sheet.hidden = false;

    /* Commit the hidden -> shown change as its own styling pass, or
       the browser coalesces both and the sheet jumps to its end state
       instead of sliding up. Reading offsetWidth is synchronous; a
       requestAnimationFrame here was measured NOT firing on the live
       site even with document.visibilityState "visible", which left
       .is-open unset and the sheet invisible while its backdrop went
       on swallowing taps. */
    void sheet.offsetWidth;
    sheet.classList.add('is-open');

    lockScroll(!panel);

    /* preventScroll is the whole point of this call's options.

       Moving focus into the sheet makes the browser scroll the
       focused control into view, and the sheet is pinned to the
       bottom of the viewport -- so a real click on a delete control
       part-way down the cart yanked the page. Measured on the cart
       page: 900 -> 666, a 234px jump, every time.

       It does not reproduce with a scripted element.click(), which is
       why it survived the earlier passes: the scroll-into-view only
       fires for genuine user-initiated focus. Test this one with a
       real pointer click.

       Focus still moves, so keyboard and screen-reader users land on
       the first action as before; only the scrolling is suppressed. */
    var first = sheet.querySelector(canWishlist ? '[data-uic-choice="wishlist"]' : '[data-uic-choice="remove"]');
    if (first) {
      try {
        first.focus({ preventScroll: true });
      } catch (e) {
        // Very old browsers ignore the options object entirely.
        first.focus();
      }
    }
  }

  function close() {
    if (!sheet || sheet.hidden) return;

    sheet.classList.remove('is-open');
    unlockScroll();
    pending = null;

    var el = sheet;
    setTimeout(function () {
      if (!el.classList.contains('is-open')) el.hidden = true;
    }, 220);

    if (lastFocused && lastFocused.isConnected && lastFocused.focus) lastFocused.focus();
  }

  /* ---- Interception ---- */

  document.addEventListener(
    'click',
    function (event) {
      var btn = event.target.closest('cart-remove-button');
      if (!btn) return;

      // Our own re-dispatch: let it through to cart.js untouched.
      if (btn[ALLOW_FLAG]) {
        btn[ALLOW_FLAG] = false;
        return;
      }

      // Nothing to remove from, so leave the markup to behave as it would.
      if (!cartItemsFor(btn)) return;

      event.preventDefault();
      event.stopPropagation();
      open(btn);
    },
    true
  );

  /* ---- Choice ---- */

  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-uic-choice-cancel]')) {
      close();
      return;
    }

    var choice = event.target.closest('[data-uic-choice]');
    if (!choice || !pending) return;

    var btn = pending;
    var action = choice.getAttribute('data-uic-choice');

    if (action === 'remove') {
      close();
      doRemove(btn);
      return;
    }

    if (action === 'wishlist') {
      var result = window.UICWishlist.save(
        btn.getAttribute('data-uic-product-id'),
        btn.getAttribute('data-uic-product-handle')
      );

      close();

      /* 'login' means the save has NOT happened yet -- the login
         prompt is open and the product is carried through the round
         trip. Removing the line now would drop it on the floor if the
         customer signs in and comes back, so leave the cart alone. */
      if (result === 'login') return;

      doRemove(btn);
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') close();
  });

  /* Both carts replace their markup wholesale on every update, so the
     element this sheet is holding can stop existing underneath it. */
  document.addEventListener('cart:refresh', close);
  new MutationObserver(function () {
    if (pending && !pending.isConnected) close();
  }).observe(document.body, { childList: true, subtree: true });
})();
